import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, ViewChild} from '@angular/core';
import {filter, Subscription, take} from "rxjs";
import {EventTypes} from "../../event-types";
import {newSearch} from "../../state/search/search.actions";
import {selectNewSearchFailure, selectNewSearchSuccess} from "../../state/search/search.selectors";
import {AuthService} from "../../services/auth.service";
import {Store} from "@ngrx/store";
import {AppState} from "../../state/app.state";
import {SharedService} from "../../services/shared.service";
import {PopupService} from "../../services/popup.service";
import {animate, state, style, transition, trigger} from "@angular/animations";
import { SearchPersonDropdownComponent } from './search-person-dropdown/search-person-dropdown.component';
import {Route, Router} from "@angular/router";
import {RouterTestingHarness} from "@angular/router/testing";
import {ReportBannerService} from "../../services/report-banner.service";

@Component({
  selector: 'app-search-person',
  templateUrl: './search-person.component.html',
  styleUrl: './search-person.component.scss',
  animations: [
    trigger('borderAnimation', [
      state('true', style({
        borderColor: 'red'
      })),
      state('false', style({
        borderColor: 'transparent'
      })),
      transition('false => true', [
        animate('0.2s')
      ]),
      transition('true => false', [
        animate('0.2s')
      ])
    ])
  ]
})
export class SearchPersonComponent implements OnInit, AfterViewInit {

  @Input() componentLocation: string = 'reports';
  @Input() workspaceId?: string;
  @Output() searchCompleted = new EventEmitter<void>();
  @ViewChild('inputElement') inputElement: ElementRef;
  @ViewChild('linkedinInput') linkedinInput: ElementRef;

  errorMessage = '';
  linkedin = '';
  linkedinUrlValid = false;
  showDropdown = false;

  user: any;

  newSearchSuccessSubscription: Subscription = null;
  newSearchFailureSubscription: Subscription = null;

  private subscription: Subscription;
  private isSearchingInThisComponent: boolean = false; // Track if this component initiated a search

  @Input() isVisible: boolean = false;
  @Output() modalClosed: EventEmitter<void> = new EventEmitter();

  @ViewChild('dropdown') dropdownRef: SearchPersonDropdownComponent;

  @Output() dropdownVisibilityChanged = new EventEmitter<boolean>();

  constructor(
    private authService : AuthService,
    private popupService: PopupService,
    private store: Store<AppState>,
    private sharedService: SharedService,
    private renderer: Renderer2,
    private routerService: Router,
    private reportBannerService: ReportBannerService,
    private hostRef: ElementRef<HTMLElement>
  ) {}

  /**
   * Close the dropdown on any click outside this component. Without it the dropdown
   * stays open for as long as there is text in the input, and consumers that dim the
   * page while it is open (reports, workspace reports) keep swallowing clicks on the
   * content underneath. The typed query is kept — only the dropdown is dismissed.
   *
   * Bound in the **capture** phase rather than via `@HostListener('document:click')`:
   * handlers that call `stopPropagation()` (the reports row's export checkbox does, and
   * it sits above the dimming overlay) would otherwise stop the click from ever reaching
   * the document, leaving both the dropdown and the overlay stuck open.
   */
  private readonly onDocumentClickCapture = (event: Event): void => {
    this.onDocumentClick(event as MouseEvent);
  };

  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;

    if (target && this.hostRef.nativeElement.contains(target)) {
      // Clicking back into the input re-opens the results for the query that is
      // still there — otherwise dismissing once would hide them until retyping.
      // Restricted to the input so a click on a result cannot resurrect the
      // dropdown that onDropdownSelect() just closed.
      if (!this.showDropdown && target instanceof HTMLInputElement && !!this.linkedin.trim() && !this.linkedinUrlValid) {
        this.showDropdown = true;
        this.dropdownVisibilityChanged.emit(this.showDropdown);
      }

      return;
    }

    this.closeDropdown();
  }

  /** Hide the dropdown (and notify the parent) without clearing the query. */
  closeDropdown(): void {
    if (!this.showDropdown) {
      return;
    }

    this.showDropdown = false;
    this.dropdownVisibilityChanged.emit(this.showDropdown);
  }

  ngOnInit(): void {

    this.authService.authUser().subscribe(user => {
      this.user = user;
    });

    if (this.componentLocation === 'dashboard') {
      this.toggleBodyScroll(this.isVisible);
    }

    this.subscription = this.sharedService.searchPersonEventEmitter.subscribe(event => {
      if (event.type === EventTypes.SHOW_REPORT_BANNER) {
        const reportId = event.data.report_id;

        // Only navigate if THIS component initiated the search
        // This prevents other tabs from being redirected when a new report is created
        if (this.isSearchingInThisComponent) {
          this.routerService.navigate(['/report/' + reportId]);
          this.isSearchingInThisComponent = false; // Reset the flag
        }
      }

      if (event.type === EventTypes.SEARCH_ERROR) {
        this.isSearchingInThisComponent = false;
      }
    });

  }

  ngAfterViewInit(): void {

    document.addEventListener('click', this.onDocumentClickCapture, true);

    if (this.componentLocation === 'dashboard') {
      // Set focus to the input element after the view initializes
      if (this.inputElement) {
        this.inputElement.nativeElement.focus();
      }
    }

  }

  ngOnDestroy(): void {

    document.removeEventListener('click', this.onDocumentClickCapture, true);

    if (this.componentLocation === 'dashboard') {
      this.toggleBodyScroll(false);
    }

    // Clean up all subscriptions
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    if (this.newSearchSuccessSubscription) {
      this.newSearchSuccessSubscription.unsubscribe();
    }

    if (this.newSearchFailureSubscription) {
      this.newSearchFailureSubscription.unsubscribe();
    }

  }

  ngOnChanges(): void {

    if (this.componentLocation === 'dashboard') {
      this.toggleBodyScroll(this.isVisible);

      if (this.isVisible) {
        this.setFocus();
      }

    }

  }

  setFocus() {
    setTimeout(() => {
      this.linkedinInput?.nativeElement.focus();
    }, 0);
  }

  // Handle input change (only show dropdown when typing)
  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value;          // preserve exactly what the user typed (spaces incl.)
    const trimmed = raw.trim();       // used only for validation / search decisions

    this.linkedin = raw;
    this.linkedinUrlValid = this.isLinkedinUrl(trimmed);

    // Show dropdown only if the input is not a LinkedIn URL and is not empty
    this.showDropdown = !this.linkedinUrlValid && !!trimmed;
    this.dropdownVisibilityChanged.emit(this.showDropdown);
  }

  // Handle dropdown result selection
  /*
  onDropdownSelect(value: string): void {
    this.linkedin = value;
    this.linkedinUrlValid = this.isLinkedinUrl(this.linkedin);
    this.showDropdown = false; // Hide dropdown after selection
  }
  */

  // This method will be triggered when the child (dropdown) emits its event
  onDropdownSelect(linkedinUrl: string): void {
    // console.log('[search-person.component.ts] onDropdownSelect')
    // console.log('[search-person.component.ts] linkedinUrl', linkedinUrl)

    this.linkedin = linkedinUrl;

    this.linkedinUrlValid = this.isLinkedinUrl(this.linkedin);
    // console.log('this.linkedinUrlValid', this.linkedinUrlValid)

    this.processSearch();

    this.showDropdown = false;
    this.dropdownVisibilityChanged.emit(this.showDropdown);
  }

  private updateSearch(value: string): void {
    this.linkedin = value;
    this.linkedinUrlValid = this.isLinkedinUrl(this.linkedin);

    if (this.linkedinUrlValid) {
      this.showDropdown = false;
      this.dropdownVisibilityChanged.emit(this.showDropdown);
    } else {
      this.showDropdown = !!this.linkedin;
      this.dropdownVisibilityChanged.emit(this.showDropdown);
    }
  }

  // Check if the input is a valid LinkedIn URL
  isLinkedinUrl(value: string): boolean {
    // Explanation:
    // ^            => start of string
    // https?       => 'http' or 'https'
    // :\/\/        => literal ://
    // www\.linkedin\.com\/ => must match 'www.linkedin.com/'
    // (?!.*https?:\/\/www\.linkedin\.com\/) => negative lookahead for repeating that full string
    // .*          => anything else
    // $           => end of string
    const linkedinRegex = /^https?:\/\/www\.linkedin\.com\/(?!.*https?:\/\/www\.linkedin\.com\/).*$/;
    return linkedinRegex.test(value);
  }

  /* commented on 11.01.2025
  processSearch() {
    const linkedinRegex = /^https:\/\/www\.linkedin\.com\/(?!.*https:\/\/www\.linkedin\.com\/).*$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Trim the input to remove leading and trailing whitespace
    const trimmedInput = this.linkedin.trim();

    if (linkedinRegex.test(trimmedInput) || emailRegex.test(trimmedInput)) {
      this.errorMessage = "";
      this.commitSearch();

      if (this.componentLocation === 'dashboard') {
        // Notify parent component that the search is completed
        this.searchCompleted.emit();
      }

    } else {
      this.errorMessage = 'Invalid LinkedIn profile or email address. Please try again.';
    }
  }
  */

  // Called when the search button is clicked
  processSearch(): void {
    if (this.linkedinUrlValid) {
      // console.log('Searching for LinkedIn URL:', this.linkedin);
      // this.searchCompleted.emit();
      this.commitSearch()
    }
  }

  commitSearch() {
    if (this.componentLocation === 'dashboard') {
      this.closeModal();
    }

    // CRITICAL: Forcefully disable bulk upload mode WITHOUT closing banners
    // This ensures individual report banners will show correctly
    // Using forceDisable instead of setBulkUploadMode to avoid premature banner closure
    this.reportBannerService.forceDisableBulkUploadMode();

    // Store the current LinkedIn profile being searched
    const currentLinkedInProfile = this.linkedin.trim();

    // Mark that this component is initiating a search
    this.isSearchingInThisComponent = true;

    this.sharedService.emitEvent(EventTypes.SHOW_LOADING_REPORT_BANNER, { message: 'Loading Report Banner' });

    this.store.dispatch(newSearch({
      user_id: this.user.id,
      linkedin_profile: currentLinkedInProfile,
      workspace_id: this.workspaceId
    }));

    this.linkedin = '';

    // Listen for the all searches success or failure from the store
    const newSearchSuccess$ = this.store.select(selectNewSearchSuccess);
    const newSearchFailure$ = this.store.select(selectNewSearchFailure);

    // Unsubscribe from any existing subscriptions first
    if (this.newSearchSuccessSubscription) {
      this.newSearchSuccessSubscription.unsubscribe();
      this.newSearchSuccessSubscription = null;
    }

    // Subscribe to the all searches success
    this.newSearchSuccessSubscription = newSearchSuccess$.pipe(
      filter(response => response !== null),
      // Make sure this is the response for our most recent search
      filter(response => response.search?.linkedin_profile === currentLinkedInProfile),
      take(1)
    ).subscribe({
      next: (response) => {
        console.log('[search-person.component.ts] newSearchSuccessSubscription', response);

        // Check if the report exists with any valid status
        if (response.search && response.search.search_status_id) {

          // Only close the banner if the report is already completed (status 3)
          // If it's still being processed (status !== 3), leave the banner open
          // so the "Report has finished" banner can show when the WebSocket event arrives
          if (response.search.search_status_id === 3) {
            this.sharedService.emitEvent(EventTypes.CLOSE_REPORT_BANNER, { message: 'Close Report Banner' });
          }

          // Redirect to the report page (existing report found)
          this.routerService.navigate(['/report', response.search.id]);

          // Reset the flag since we're done with this search
          this.isSearchingInThisComponent = false;
        }

        // Clean up subscription
        if (this.newSearchSuccessSubscription) {
          this.newSearchSuccessSubscription.unsubscribe();
          this.newSearchSuccessSubscription = null;
        }
      },
      error: (error) => {
        console.error('Search error:', error);

        this.sharedService.emitEvent(EventTypes.CLOSE_REPORT_BANNER, { message: 'Close Report Banner' });

        // Reset the flag on error
        this.isSearchingInThisComponent = false;

        if (this.newSearchSuccessSubscription) {
          this.newSearchSuccessSubscription.unsubscribe();
          this.newSearchSuccessSubscription = null;
        }
      }
    });

    // Same with error subscription
    if (this.newSearchFailureSubscription) {
      this.newSearchFailureSubscription.unsubscribe();
      this.newSearchFailureSubscription = null;
    }

    // Subscribe to the search failure
    this.newSearchFailureSubscription = newSearchFailure$.pipe(
      filter(failure => failure !== null),
      take(1)
    ).subscribe({
      next: (error) => {
        const errorMessage = error.error.message;
        this.popupService.notify(errorMessage, 'Search error', 'error');
        this.sharedService.emitEvent(EventTypes.CLOSE_REPORT_BANNER, { message: 'Close Report Banner' });

        // Reset the flag on failure
        this.isSearchingInThisComponent = false;

        if (this.newSearchFailureSubscription) {
          this.newSearchFailureSubscription.unsubscribe();
          this.newSearchFailureSubscription = null;
        }
      },
      error: (error) => {
        console.error('Search error handling error:', error);
        this.sharedService.emitEvent(EventTypes.CLOSE_REPORT_BANNER, { message: 'Close Report Banner' });

        // Reset the flag on error
        this.isSearchingInThisComponent = false;

        if (this.newSearchFailureSubscription) {
          this.newSearchFailureSubscription.unsubscribe();
          this.newSearchFailureSubscription = null;
        }
      }
    });
  }

  closeModal() {
    this.isVisible = false;
    this.modalClosed.emit();
  }

  clearInput(): void {
    this.linkedin = ''
  }

  private toggleBodyScroll(block: boolean): void {
    if (block) {
      this.renderer.addClass(document.body, 'overflow-hidden');
    } else {
      this.renderer.removeClass(document.body, 'overflow-hidden');
    }
  }

  clearAll(): void {
    this.clearInput()

    if (this.dropdownRef) {
      this.dropdownRef.clearResults();
    }

    this.showDropdown = false
    this.dropdownVisibilityChanged.emit(this.showDropdown);
  }

}
